import { v2Client } from "@/utils/axios";
import { getCurrentAuth } from "@/hooks/useAxiosPrivate";
import { APIRoutes } from "@/utils/constants";

/** Lifecycle of an uploaded video (HTPTV2-89). Only `ready` is schedulable. */
export type UploadStatus = "uploading" | "pending" | "processing" | "ready" | "failed";

/** One uploaded video as the admin API serializes it. */
export interface UploadRow {
  id: number;
  assetId: string;
  title: string;
  description: string | null;
  status: UploadStatus;
  manifestUrl: string | null;
  thumbnail: string | null;
  durationSec: number | null;
  fileName: string | null;
  sizeBytes: number | null;
  failureReason: string | null;
  createdAt: string;
}

/** Status while still uploading carries what a resume needs. */
export interface UploadStatusRow extends UploadRow {
  uploadedParts?: number[];
  missingParts?: number[];
  totalParts?: number;
  partSizeBytes?: number;
}

export interface CreatedUpload {
  assetId: string;
  uploadId: string;
  partSizeBytes: number;
  totalParts: number;
}

export interface UploadedPart {
  PartNumber: number;
  ETag: string;
}

function authHeaders(): Record<string, string> {
  const token = getCurrentAuth()?.AccessToken;
  return token ? { Authorization: `bearer ${token}` } : {};
}

/** The API's message for a failed call (its Boom body), else a fallback. */
export function uploadErrorMessage(e: unknown, fallback: string): string {
  const body = (e as { response?: { data?: { message?: string } } })?.response?.data;
  return body?.message || (e instanceof Error && e.message) || fallback;
}

const base = APIRoutes.AdminUploads;

/** One page of the library, newest first. */
export interface UploadsPage {
  uploads: UploadRow[];
  hasMore: boolean;
  nextOffset: number;
}

export const UPLOADS_PAGE_SIZE = 24;

/** One page of uploads; the title search runs on the server. */
export async function listUploads(search = "", offset = 0): Promise<UploadsPage> {
  const res = await v2Client.get<{ data: Partial<UploadsPage> }>(base, {
    params: { ...(search ? { search } : {}), limit: UPLOADS_PAGE_SIZE, offset },
    headers: authHeaders(),
  });
  const data = res.data?.data ?? {};
  const uploads = data.uploads ?? [];
  return {
    uploads,
    // An API without paging returns everything in one go: no next page.
    hasMore: Boolean(data.hasMore),
    nextOffset: data.nextOffset ?? offset + uploads.length,
  };
}

export async function createUpload(body: {
  title: string;
  description: string | null;
  fileName: string;
  fileSize: number;
  mimeType: string;
}): Promise<CreatedUpload> {
  const res = await v2Client.post<{ data: CreatedUpload }>(base, body, { headers: authHeaders() });
  return res.data.data;
}

/** Presigned S3 URLs for a batch of part numbers (the API caps a batch at 20). */
export async function signParts(assetId: string, partNumbers: number[]): Promise<Record<string, string>> {
  const res = await v2Client.post<{ data: { urls: Record<string, string> } }>(
    `${base}/${assetId}/sign-parts`,
    { partNumbers },
    { headers: authHeaders() },
  );
  return res.data.data.urls;
}

export async function getUploadStatus(assetId: string): Promise<UploadStatusRow> {
  const res = await v2Client.get<{ data: UploadStatusRow }>(`${base}/${assetId}/status`, {
    headers: authHeaders(),
  });
  return res.data.data;
}

export async function completeUpload(assetId: string, parts: UploadedPart[]): Promise<UploadRow> {
  const res = await v2Client.post<{ data: UploadRow }>(
    `${base}/${assetId}/complete`,
    { parts },
    { headers: authHeaders() },
  );
  return res.data.data;
}

export async function abortUpload(assetId: string): Promise<void> {
  await v2Client.post(`${base}/${assetId}/abort`, {}, { headers: authHeaders() });
}

export async function deleteUpload(assetId: string): Promise<void> {
  await v2Client.delete(`${base}/${assetId}`, { headers: authHeaders() });
}
